import { Field, FieldProps } from 'formik';
import CustomSelectField from 'Common/components/CustomSelectField';
import { KeyboardEventHandler, RefObject } from 'react';

type CustomInputProps = {
  label?: string;
  help?: string;
  name: string
  autocomplete?: string
  placeholder?: string
  type?: string
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  innerRef?: RefObject<HTMLInputElement>
  testId?: string;
}

const currencyOptions = [
  { value: 'usd', label: 'USD - United States Dollar' },
  { value: 'cad', label: 'CAD - Canadian Dollar' },
  { value: 'mxn', label: 'MXN - Mexican Peso' },
  { value: 'brl', label: 'BRL - Brazilian Real' },
  { value: 'ars', label: 'ARS - Argentine Peso' },
  { value: 'clp', label: 'CLP - Chilean Peso' },
  { value: 'cop', label: 'COP - Colombian Peso' },
  { value: 'pen', label: 'PEN - Peruvian Sol' },
  { value: 'ves', label: 'VES - Venezuelan Bolívar' },
  { value: 'eur', label: 'EUR - Euro' },
  { value: 'gbp', label: 'GBP - British Pound' },
  { value: 'chf', label: 'CHF - Swiss Franc' },
  { value: 'sek', label: 'SEK - Swedish Krona' },
  { value: 'nok', label: 'NOK - Norwegian Krone' },
  { value: 'dkk', label: 'DKK - Danish Krone' },
  { value: 'pln', label: 'PLN - Polish Zloty' },
  { value: 'czk', label: 'CZK - Czech Koruna' },
  { value: 'huf', label: 'HUF - Hungarian Forint' },
  // Add more currencies as needed
];

export const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'MX', label: 'Mexico' },
  { value: 'BR', label: 'Brazil' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PE', label: 'Peru' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'PT', label: 'Portugal' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'PL', label: 'Poland' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'HU', label: 'Hungary' },
  { value: 'AT', label: 'Austria' },
  { value: 'IE', label: 'Ireland' },
  // Add more countries as needed
];

export const CustomInputField: React.FC<CustomInputProps> = ({
  label,
  name,
  help,
  type,
  placeholder,
  autocomplete,
  onKeyDown,
  innerRef,
  testId,
}) => {
  return (
    <label> {label}
      <Field name={name}>
        {({ field, meta }: FieldProps) => (
          <div
            className="relative mb-6"
          >
            <input
              data-testid={testId}
              data-cy={testId}
              {...field}
              autoComplete={autocomplete}
              placeholder={placeholder}
              type={type}
              onKeyDown={onKeyDown}
              ref={innerRef}
              className="input-lg"
            />
            <span
              className={`absolute text-sm ${meta.touched ? 'error' : ''}`}
            >
              {(meta.touched && meta.error) && meta.error}
              {!meta.touched && help}
            </span>
          </div>
        )}
      </Field>
    </label>
  );
};

export const CurrencyInput = () => {
  return (
    <CustomSelectField
      options={currencyOptions}
      label='Currency'
      name='currency'
      defaultValue='cad'
      help='You cannot change your currency later'
    />
  );
};

export const CountryInput = () => {
  return <CustomSelectField
    options={countryOptions}
    label='Country'
    name='country'
    defaultValue='CA'
    help='You cannot change your country later'
  />;
};

export const BusinessPropertyNameInput = ({ label }: { label: string }) => {
  return <CustomInputField
    label={label}
    name='business.name'
    type='text'
  />;
};

export const BusinessNumEmployessInput = () => {
  return <CustomInputField
    label='Enter number of Employees or Residents'
    name='business.numEmployees'
    type='number'
  />;
};

const countryCurrencyMap = {
  US: 'usd',
  CA: 'cad',
  MX: 'mxn',
  BR: 'brl',
  AR: 'ars',
  CL: 'clp',
  CO: 'cop',
  PE: 'pen',
  VE: 'ves',
  GB: 'gbp',
  FR: 'eur',
  DE: 'eur',
  ES: 'eur',
  IT: 'eur',
  NL: 'eur',
  BE: 'eur',
  PT: 'eur',
  SE: 'sek',
  CH: 'chf',
  NO: 'nok',
  DK: 'dkk',
  FI: 'eur',
  PL: 'pln',
  CZ: 'czk',
  HU: 'huf',
  AT: 'eur',
  IE: 'eur',
};

export const getCurrencyFromCountry = (countryCode: string) => {
  const currencyCode = countryCurrencyMap[countryCode as keyof typeof countryCurrencyMap] || 'cad';
  const currency = currencyOptions.find(option => option.value === currencyCode);

  if (!currency) {
    console.warn(`Currency not found for country code: ${countryCode}. Defaulting to CAD.`);
    return currencyOptions.find(option => option.value === 'cad');
  }

  return currency;
};
